import {
  InsuranceAdvice,
  InsuranceCoverage,
  PaymentReason,
  PurchaseRecommendation,
  RecommendationReason,
  UserAuthorizationLevel,
} from "Store/advice/interfaces";
import {
  AdviceProductDetailsFormValues,
  DebtResponsible,
  FamilySituation,
} from "../../../ProductDetails/FormElements/interfaces";
import { getAdviceRestrictions, getCorrectPaymentValue } from "../utils";

import { VALUE_TYPE } from "Components/NumberValue";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { formatToLocaleString } from "Formatting/formatNumber";
import { isDefined } from "Utils/functions";
import React from "react";
import { AdviceTextsResult } from "./getAdviceTexts";

export const generateAdviceTextsDeath = (
  isAdvisor: boolean,
  insuranceAdvice: InsuranceAdvice,
  adviceTexts: any,
  lang: string,
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): AdviceTextsResult => {
  const { singlePayment, recommendedCoverage } = insuranceAdvice;
  const adviceValue = getCorrectPaymentValue(
    UserAuthorizationLevel.PRODUCT_MANAGER,
    singlePayment,
  );
  const paymentReasons = singlePayment.advice?.paymentReasons ?? {};
  const causes = singlePayment.recommendationConfiguration.causes;
  const advisorReachedMaxPayout =
    isAdvisor &&
    adviceValue === getAdviceRestrictions(insuranceAdvice).maxProductManager;
  const shouldPurchaseFromAdvisor =
    !isAdvisor &&
    singlePayment.recommendationConfiguration.recommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR;

  const insuranceDesc = (): React.ReactElement => {
    const { desc } = adviceTexts;

    if (advisorReachedMaxPayout) {
      return (
        <Description fw={400} size="14px">
          {`${desc.part1.weAdvice[lang]}${formatToLocaleString(adviceValue)} ${
            VALUE_TYPE.NOK
          }. ${desc.part1.maximumAmount[lang]}`}
        </Description>
      );
    }

    const becauseOf = desc.part1.becauseOf[lang];
    let yourEconomicalSituation = "";
    if (recommendedCoverage === InsuranceCoverage.BASIC) {
      yourEconomicalSituation = desc.part1.yourEconomicalSituation.basic[lang];
    } else if (recommendedCoverage === InsuranceCoverage.PLUS) {
      yourEconomicalSituation = desc.part1.yourEconomicalSituation.plus[lang];
    } else if (recommendedCoverage === InsuranceCoverage.TOP) {
      yourEconomicalSituation = desc.part1.yourEconomicalSituation.top[lang];
    }

    const weAdvice = shouldPurchaseFromAdvisor
      ? desc.part1[lang]
      : `${desc.part1.weAdvice[lang]}${formatToLocaleString(adviceValue)} ${
          VALUE_TYPE.NOK
        }${becauseOf}${yourEconomicalSituation}${desc.part1.whichCovers[lang]}`;

    let forYourSituation = "";
    const debt = isDefined(paymentReasons[PaymentReason.DEBT_DEPENDENTS]);
    const children = isDefined(paymentReasons[PaymentReason.MINOR_CHILDREN]);
    const familySituation = adviceProductIntroFormValues.familySituation;
    const debtResponsible = adviceProductIntroFormValues.debtResponsible;
    const notAlone =
      familySituation !== FamilySituation.SINGLE &&
      debtResponsible &&
      debtResponsible !== DebtResponsible.ME_ALONE;
    if (debt && children) {
      if (notAlone) {
        forYourSituation = desc.part2.youBoth.debtAndChildren[lang];
      } else {
        forYourSituation = desc.part2.youAlone.debtAndChildren[lang];
      }
    } else if (debt) {
      if (notAlone) {
        forYourSituation = desc.part2.youBoth.debt[lang];
        if (
          debtResponsible === DebtResponsible.BOTH &&
          recommendedCoverage === InsuranceCoverage.BASIC
        ) {
          forYourSituation = `${desc.part2.youBoth.debt.halfDebt[lang]}${desc.part2.youBoth.debt[lang]}`;
        }
      } else {
        forYourSituation = desc.part2.youAlone.debt[lang];
      }
    } else if (children) {
      if (notAlone) {
        forYourSituation = desc.part2.youBoth.children[lang];
      } else {
        forYourSituation = desc.part2.youAlone.children[lang];
      }
    } else {
      forYourSituation = desc.part2.default[lang];
      if (debtResponsible === DebtResponsible.SPOUSE_COHABITANT_ALONE) {
        forYourSituation = `${desc.part2.default[lang]}${desc.part2.default.spouseCohabitantAlone[lang]}`;
      }
    }

    let inAdditionItCovers = "";
    if (isDefined(paymentReasons[PaymentReason.INCOME_DEPENDENTS])) {
      const aValueBeing = desc.part4.subPart1[lang];
      let amount = "";
      if (recommendedCoverage === InsuranceCoverage.TOP) {
        amount = desc.part4.subPart2.top[lang];
      } else if (
        recommendedCoverage === InsuranceCoverage.PLUS ||
        recommendedCoverage === InsuranceCoverage.BASIC
      ) {
        amount = desc.part4.subPart2.notTop[lang];
      }
      const ofYearlyIncome = desc.part4.subPart3[lang];
      let sinceYourPartner = "";
      if (familySituation === FamilySituation.COHABITANT) {
        sinceYourPartner = desc.part4.subPart4.cohabitant[lang];
      } else if (familySituation === FamilySituation.MARRIED) {
        sinceYourPartner = desc.part4.subPart4.married[lang];
      }
      const isIncomeDependent = desc.part4.subPart5[lang];
      inAdditionItCovers = `${aValueBeing}${amount}${ofYearlyIncome}${sinceYourPartner}${isIncomeDependent}`;
    }

    return (
      <Description fw={400} size="14px">
        {`${weAdvice}${forYourSituation}${inAdditionItCovers}${
          shouldPurchaseFromAdvisor
            ? `${becauseOf}${yourEconomicalSituation}.`
            : "."
        }`}
      </Description>
    );
  };

  return {
    header: adviceTexts.header[lang],
    title: adviceTexts.title[lang],
    desc: isDefined(recommendedCoverage) ? (
      insuranceDesc()
    ) : (
      <Description fw={400} size="14px">
        {causes?.includes(RecommendationReason.DEPENDENTS) &&
          adviceTexts.adviceUnavailableMessages.deathDependents[lang]}
      </Description>
    ),
  };
};
